import React, { useState, useEffect } from "react";
import { useHRMS } from "../context/HRMSContext.jsx";
import api from "../services/api.js";
import DatePicker from "../components/DatePicker.jsx";
import Message from "../components/Message.jsx";
import PermissionGuard from "../components/PermissionGuard.jsx";
import { MODULES, ACTIONS } from "../context/PermissionsContext.jsx";

function Payroll() {
  const { state, loadPayroll } = useHRMS();
  const [showPayrollForm, setShowPayrollForm] = useState(false);
  const [formData, setFormData] = useState({
    workerName: "",
    joinDt: "",
    workDays: "",
    lastDays: "",
    persLeave: "",
  });
  const [selectedworker_id, setSelectedworker_id] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [payrollFilters, setPayrollFilters] = useState({
    workerName: "",
    joinDt: "",
    workDays: "",
  });
  const [summary, setSummary] = useState({
    salary: 0,
    totalWorkingDays: 0,
    totalWorkingHours: 0,
    totalAttendance: 0,
    totalLeaves: 0,
    totalHoursWorked: 0,
    payForRegular: 0,
    totalOTHours: 0,
    payForOT: 0,
    ssoPayment: 0,
    totalPayableAmount: 0,
    loanDeduction: 0,
    bonusAdd: 0,
    extraPay: 0,
  });

  const [message, setMessage] = useState({ text: "", type: "" });
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Set default date range to current month only if not already set
  useEffect(() => {
    if (!startDate || !endDate) {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      // Ensure end date doesn't exceed current date
      const today = new Date();
      const actualEndDate = lastDay > today ? today : lastDay;

      setStartDate(firstDay.toISOString().split("T")[0]);
      setEndDate(actualEndDate.toISOString().split("T")[0]);
    }
  }, [startDate, endDate]);
  useEffect(() => {
    loadPayroll();
  }, [loadPayroll]);

  // Load payment terms
  useEffect(() => {
    const fetchPaymentTerms = async () => {
      try {
        const terms = await api.paymentTerms.getAll();
        setPaymentTerms(terms);
      } catch (error) {
        console.error("Error loading payment terms:", error);
      }
    };
    fetchPaymentTerms();
  }, []);

  const fetchWorkerAttendance = async () => {
    if (!selectedworker_id) return;

    setLoading(true);
    try {
      // Fetch full worker details to get bonus_amount
      const fullWorkerData = await api.workers.getById(selectedworker_id);

      const data = await api.payroll.getWorkerAttendance(
        selectedworker_id,
        startDate,
        endDate,
      );

      setAttendanceData(data.attendance || []);
      calculateSummary(data.attendance || [], data.worker, fullWorkerData);

      // Auto-fill worker name and joining date if available
      if (data.worker && !formData.workerName) {
        const worker = state.workers?.find(
          (worker) => worker.id === parseInt(selectedworker_id),
        );
        if (worker) {
          setFormData((prev) => ({
            ...prev,
            workerName: worker.name,
            joinDt: worker.joinDate || "",
          }));
        }
      }
    } catch (error) {
      setAttendanceData([]);
      setMessage({
        text: `Error loading attendance data: ${error.message || "Unknown error"}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch attendance data when worker is selected
  useEffect(() => {
    if (selectedworker_id && startDate && endDate) {
      fetchWorkerAttendance();
    } else {
      setAttendanceData([]);
      setSummary({
        salary: 0,
        totalWorkingDays: 0,
        totalWorkingHours: 0,
        totalAttendance: 0,
        totalLeaves: 0,
        totalHoursWorked: 0,
        payForRegular: 0,
        totalOTHours: 0,
        payForOT: 0,
        ssoPayment: 0,
        totalPayableAmount: 0,
        loanDeduction: 0,
        bonusAdd: 0,
        extraPay: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedworker_id, startDate, endDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if payroll already exists for this worker and specific date range
    const payrollData = {
      ...formData,
      startDate,
      endDate,
      worker_id: selectedworker_id,
      workDays: summary.totalWorkingDays,
      persLeave: summary.totalLeaves,
      extraPay: summary.extraPay || 0,
    };

    const existingPayroll = state.payroll?.find(
      (p) =>
        p.workerName === formData.workerName &&
        p.startDate === startDate &&
        p.endDate === endDate,
    );

    if (existingPayroll) {
      setMessage({
        text: `Payroll already exists for ${formData.workerName} from ${startDate} to ${endDate}!`,
        type: "error",
      });
      return;
    }

    try {
      await api.payroll.create(payrollData);
      setMessage({ text: "Payroll saved successfully!", type: "success" });
      loadPayroll();
      handleCloseModal();
    } catch (error) {
      setMessage({
        text: "Already exist this date range payroll!",
        type: "error",
      });
    }
  };

  const calculateSummary = (
    attendanceData,
    apiWorkerData = null,
    fullWorkerData = null,
  ) => {
    if (!attendanceData || attendanceData.length === 0) return;

    const worker =
      apiWorkerData ||
      state.workers?.find(
        (worker) => worker.id === parseInt(selectedworker_id),
      );
    // Get salary and bonus from fullWorkerData (from getById API)
    const baseSalary =
      fullWorkerData?.monthly_salary ||
      fullWorkerData?.salary ||
      fullWorkerData?.monthlySalary ||
      fullWorkerData?.basicSalary ||
      0;

    // Get working hours - it's stored directly on worker object
    const shiftWorkingHours = worker?.workingHours
      ? parseFloat(worker.workingHours)
      : 8;

    const totalDays = attendanceData.length;
    const presentDays = attendanceData.filter(
      (att) => att.status === "Present",
    ).length;
    const leaveDays = attendanceData.filter(
      (att) => att.status === "Leave",
    ).length;
    // const leaveDays = attendanceData.reduce((total, att) => {
    //   if (att.status === "Leave") {
    //     return total + (att.session === "First Half" || att.session === "Second Half" ? 0.5 : 1);
    //   }
    //   return total;
    // }, 0);
    const holidayDays = attendanceData.filter(
      (att) => att.status === "Holiday",
    ).length;
    const workingDays = totalDays - holidayDays; // Exclude holidays from working days
    const totalPossibleHours = shiftWorkingHours * workingDays; // Total working hours = shift hours × working days

    let actualHoursWorked = 0;
    let otHours = 0;

    attendanceData.forEach((att) => {
      if (att.status === "Off") {
        // Day-off is always paid with full shift hours
        actualHoursWorked += shiftWorkingHours;

        // If worked on day-off, count as OT
        if (att.otLogin && att.otLogout) {
          const otLoginTime = new Date(`1970-01-01T${att.otLogin}:00`);
          let otLogoutTime = new Date(`1970-01-01T${att.otLogout}:00`);
          if (otLogoutTime <= otLoginTime) {
            otLogoutTime = new Date(`1970-01-02T${att.otLogout}:00`);
          }
          const otWorkHours = (otLogoutTime - otLoginTime) / (1000 * 60 * 60);
          otHours += otWorkHours;
        }
      } else if (att.status === "Holiday") {
        // Add shift working hours for all holidays
        actualHoursWorked += shiftWorkingHours;

        // If worked on holiday, count as OT hours
        if (att.otLogin && att.otLogout) {
          const otLoginTime = new Date(`1970-01-01T${att.otLogin}:00`);
          let otLogoutTime = new Date(`1970-01-01T${att.otLogout}:00`);

          // Handle overnight OT (logout next day)
          if (otLogoutTime <= otLoginTime) {
            otLogoutTime = new Date(`1970-01-02T${att.otLogout}:00`);
          }

          const otWorkHours = (otLogoutTime - otLoginTime) / (1000 * 60 * 60);
          otHours += otWorkHours;
        }
      } else if (att.status === "Present") {
        // If backend provided working hours, use it
        if (att.workingHours !== undefined) {
          actualHoursWorked += att.workingHours || 0;
        } else if (att.login) {
          // Calculate from login/logout, use shift end if logout missing
          const loginTime = new Date(`1970-01-01T${att.login}:00`);
          let logoutTime;

          if (att.logout) {
            logoutTime = new Date(`1970-01-01T${att.logout}:00`);
            if (logoutTime <= loginTime) {
              logoutTime = new Date(`1970-01-02T${att.logout}:00`);
            }
          } else {
            // Use shift end time if logout not marked
            const shift = state.shifts?.find((s) => s.id === worker?.shiftId);
            if (shift?.shiftEnd) {
              logoutTime = new Date(`1970-01-01T${shift.shiftEnd}:00`);
              if (logoutTime <= loginTime) {
                logoutTime = new Date(`1970-01-02T${shift.shiftEnd}:00`);
              }
            }
          }

          if (logoutTime) {
            const workedHours = (logoutTime - loginTime) / (1000 * 60 * 60);
            actualHoursWorked += Math.max(workedHours, 0);
          }
        }

        if (att.otLogin && att.otLogout) {
          const otLoginTime = new Date(`1970-01-01T${att.otLogin}:00`);
          let otLogoutTime = new Date(`1970-01-01T${att.otLogout}:00`);

          // Handle overnight OT (logout next day)
          if (otLogoutTime <= otLoginTime) {
            otLogoutTime = new Date(`1970-01-02T${att.otLogout}:00`);
          }

          const otWorkHours = (otLogoutTime - otLoginTime) / (1000 * 60 * 60);
          otHours += Math.max(otWorkHours, 0);
        }
      } else if (att.status === "Leave") {
        // Handle half day leaves with login/logout times
        if (att.halfDay === "First Half" || att.halfDay === "Second Half") {
          let workedHours = 0;

          // Calculate worked hours from login/logout times if available
          if (att.login && att.logout) {
            const loginTime = new Date(`1970-01-01T${att.login}:00`);
            let logoutTime = new Date(`1970-01-01T${att.logout}:00`);

            // Handle overnight logout
            if (logoutTime <= loginTime) {
              logoutTime = new Date(`1970-01-02T${att.logout}:00`);
            }

            workedHours = (logoutTime - loginTime) / (1000 * 60 * 60);
          }

          // Ensure worked hours don't exceed shift working hours
          const validWorkedHours = Math.min(workedHours, shiftWorkingHours);
          actualHoursWorked += validWorkedHours;

          // If paid leave and within limit, add remaining hours
          if (att.isPaid && validWorkedHours < shiftWorkingHours) {
            const remainingHours = shiftWorkingHours - validWorkedHours;
            actualHoursWorked += remainingHours;
          }
        } else {
          // Full day leave - use backend calculated working hours
          actualHoursWorked += att.workingHours || 0;
        }
      }
    });

    const dailyRate = baseSalary / 30;
    const hourlyRate = dailyRate / shiftWorkingHours; // Use shift working hours for hourly rate
    const payForRegular = actualHoursWorked * hourlyRate;
    let extraPay = 0;
    // Calculate total payForOT from backend data (includes multiplier)
    const totalPayForOT = attendanceData.reduce((total, att) => {
      return total + (att.payForOT || 0);
    }, 0);

    const ssoPayment = baseSalary * 0.05; // SSO deduction on base salary

    // Calculate bonus based on installment
    let bonusAdd = 0;
    const totalBonusAmount = parseFloat(fullWorkerData?.bonus_amount || 0);
    const bonusInstallmentId = fullWorkerData?.bonus_installment;

    if (totalBonusAmount > 0 && bonusInstallmentId) {
      const paymentTerm = paymentTerms.find(
        (term) => term.id === parseInt(bonusInstallmentId),
      );

      if (
        paymentTerm &&
        paymentTerm.installments &&
        paymentTerm.installments.length > 0
      ) {
        const firstInstallment = paymentTerm.installments[0];
        const installmentPercentage =
          firstInstallment.paymentPercentage ||
          firstInstallment.percentage ||
          0;
        bonusAdd = (totalBonusAmount * installmentPercentage) / 100;

        // console.log('📋 Bonus Payment Term:', paymentTerm);
        // console.log('📊 Bonus Installment %:', installmentPercentage);
      } else {
        bonusAdd = totalBonusAmount;
      }
    } else if (totalBonusAmount > 0) {
      bonusAdd = totalBonusAmount;
    }

    // Calculate loan deduction - use backend calculated value if available
    const loanDeduction = apiWorkerData?.loanDeduction || 0;

    // console.log('🔍 Full Worker Data:', fullWorkerData);
    // console.log('💰 Total Bonus Amount:', totalBonusAmount);
    // console.log('💵 Bonus to Add (after installment):', bonusAdd);
    // console.log('💸 Loan Deduction (from backend):', loanDeduction);

    const totalPayable =
      payForRegular +
      totalPayForOT +
      bonusAdd +
      summary.extraPay -
      ssoPayment -
      loanDeduction;

    setSummary((prev) => ({
      ...prev,
      salary: baseSalary,
      totalWorkingDays: workingDays,
      totalWorkingHours: totalPossibleHours,
      totalAttendance: presentDays,
      totalLeaves: leaveDays,
      totalHoursWorked: Math.round(actualHoursWorked * 100) / 100,
      payForRegular: Math.round(payForRegular * 100) / 100,
      totalOTHours: Math.round(otHours * 100) / 100,
      payForOT: Math.round(totalPayForOT * 100) / 100,
      ssoPayment: Math.round(ssoPayment * 100) / 100,
      loanDeduction: Math.round(loanDeduction * 100) / 100,
      bonusAdd: Math.round(bonusAdd * 100) / 100,
      totalPayableAmount: Math.round(totalPayable * 100) / 100,
    }));
  };

  const handleCloseModal = () => {
    setFormData({
      workerName: "",
      joinDt: "",
      workDays: "",
      lastDays: "",
      persLeave: "",
    });
    setStartDate("");
    setEndDate("");
    setSelectedworker_id("");
    setAttendanceData([]);
    setIsViewOnly(false);
    setStartDate("");
    setEndDate("");
    // Don't reset dates when closing modal
    setSummary({
      salary: 0,
      totalWorkingDays: 0,
      totalWorkingHours: 0,
      totalAttendance: 0,
      totalLeaves: 0,
      totalHoursWorked: 0,
      payForRegular: 0,
      totalOTHours: 0,
      payForOT: 0,
      ssoPayment: 0,
      totalPayableAmount: 0,
      loanDeduction: 0,
      bonusAdd: 0,
    });
    setShowPayrollForm(false);
  };

  const filteredPayroll = Array.isArray(state.payroll)
    ? state.payroll.filter((payroll) => {
        const matchesWorkerName =
          !payrollFilters.workerName ||
          String(payroll.workerName || "")
            .toLowerCase()
            .includes(payrollFilters.workerName.toLowerCase());

        const matchesJoiningDate =
          !payrollFilters.joinDt ||
          String(payroll.joinDt || "") === payrollFilters.joinDt;

        const matchesWorkingDays =
          !payrollFilters.workDays ||
          parseFloat(payroll.workDays) === parseFloat(payrollFilters.workDays);

        return matchesWorkerName && matchesJoiningDate && matchesWorkingDays;
      })
    : [];

  const hasActiveFilters = Object.values(payrollFilters).some(
    (value) => String(value).trim() !== "",
  );

  return (
    <PermissionGuard
      module={MODULES.PAYROLL}
      action={ACTIONS.VIEW}
      submodule={MODULES.PAYROLL.submodules.SALARY_CALCULATION}
    >
      <div className="payroll">
        {message.text && (
          <Message
            message={message.text}
            type={message.type}
            onClose={() => setMessage({ text: "", type: "" })}
          />
        )}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h2 className="hd_a">Payroll Management</h2>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className={`btn ${hasActiveFilters ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setShowFilters((prev) => !prev)}
              title="Filter Payroll"
            >
              <i className="bi bi-funnel"></i>
            </button>
            <PermissionGuard
              module={MODULES.PAYROLL}
              action={ACTIONS.CREATE}
              submodule={MODULES.PAYROLL.submodules.SALARY_CALCULATION}
            >
              <button
                className="btn btn-primary"
                onClick={() => {
                  setIsViewOnly(false);
                  setShowPayrollForm(true);
                }}
              >
                <i className="bi bi-plus-lg me-1"></i>Add
              </button>
            </PermissionGuard>
          </div>
        </div>

        {showFilters && (
          <div className="card shadow-sm mb-3">
            <div className="card-body py-2">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div style={{ minWidth: "200px", flex: "0 1 200px" }}>
                  <label className="form-label mb-1">Worker Name</label>
                  <select
                    className="form-control form-control-sm"
                    value={payrollFilters.workerName}
                    onChange={(e) =>
                      setPayrollFilters((prev) => ({
                        ...prev,
                        workerName: e.target.value,
                      }))
                    }
                  >
                    <option value="">All Workers</option>

                    {(state.workers || []).map((worker) => (
                      <option key={worker.id} value={worker.name}>
                        {worker.name}
                      </option>
                    ))}
                  </select>{" "}
                </div>
                <div style={{ minWidth: "170px", flex: "0 1 170px" }}>
                  <label className="form-label mb-1">Joining Date</label>
                  <DatePicker
                    value={payrollFilters.joinDt}
                    onChange={(date) =>
                      setPayrollFilters((prev) => ({
                        ...prev,
                        joinDt: date,
                      }))
                    }
                    placeholder="Select joining date"
                    className="form-control form-control-sm"
                  />
                </div>
                <div style={{ minWidth: "170px", flex: "0 1 170px" }}>
                  <label className="form-label mb-1">Total Working Days</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="form-control form-control-sm"
                    placeholder="Working days"
                    value={payrollFilters.workDays}
                    onChange={(e) =>
                      setPayrollFilters((prev) => ({
                        ...prev,
                        workDays: e.target.value,
                      }))
                    }
                  />
                </div>
                <div
                  className="d-flex align-items-end"
                  style={{ minWidth: "90px" }}
                >
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm w-100"
                    onClick={() =>
                      setPayrollFilters({
                        workerName: "",
                        joinDt: "",
                        workDays: "",
                      })
                    }
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPayrollForm && (
          <div
            className="modal"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.3)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ backgroundColor: "var(--primary-light)" }}
                >
                  <h5
                    className="modal-title"
                    style={{ color: "var(--primary-color)" }}
                  >
                    HRMS
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  <div className="row g-0">
                    <div className="col-md-8">
                      <div className="p-3">
                        <div className="row g-2 mb-3">
                          <div className="col-md-3">
                            <label className="form-label small">Worker</label>
                            <select
                              className="form-control form-control"
                              value={selectedworker_id}
                              onChange={(e) => {
                                setSelectedworker_id(e.target.value);
                                const worker = state.workers?.find(
                                  (worker) =>
                                    worker.id === parseInt(e.target.value),
                                );
                                if (worker) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    workerName: worker.name,
                                    joinDt: worker.joinDate || "",
                                  }));
                                }
                              }}
                              disabled={isViewOnly}
                              required
                            >
                              <option value="">Select Worker</option>
                              {Array.isArray(state.workers) &&
                                state.workers.map((worker) => (
                                  <option key={worker.id} value={worker.id}>
                                    {worker.name}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">
                              Start Date
                            </label>
                            <DatePicker
                              value={startDate}
                              onChange={setStartDate}
                              placeholder="Select start date"
                              className="form-control form-control-sm"
                              maxDate={new Date().toISOString().split("T")[0]}
                              disabled={isViewOnly}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">End Date</label>
                            <DatePicker
                              value={endDate}
                              onChange={setEndDate}
                              placeholder="Select end date"
                              className="form-control form-control-sm"
                              maxDate={new Date().toISOString().split("T")[0]}
                              disabled={isViewOnly}
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label small">
                              Joining Date
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={formData.joinDt}
                              readOnly
                              style={{ backgroundColor: "#f8f9fa" }}
                            />
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered">
                            <thead>
                              <tr>
                                <th>S.No.</th>
                                <th>Date</th>
                                <th>Day</th>
                                <th>Log In</th>
                                <th>Log Out</th>
                                <th>OT Log In</th>
                                <th>OT Log Out</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr>
                                  <td colSpan="8" className="text-center">
                                    Loading attendance data...
                                  </td>
                                </tr>
                              ) : attendanceData.length === 0 ? (
                                <tr>
                                  <td colSpan="8" className="text-center">
                                    {selectedworker_id
                                      ? "No attendance data found for selected date range"
                                      : "Please select a worker to view attendance"}
                                  </td>
                                </tr>
                              ) : (
                                attendanceData.map((att, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{att.date}</td>
                                    <td>{att.day}</td>
                                    <td>{att.login || "-"}</td>
                                    <td>
                                      {att.logout
                                        ? att.logout.includes(":")
                                          ? att.logout
                                          : att.logout
                                        : "-"}
                                    </td>
                                    <td>{att.otLogin || "-"}</td>
                                    <td>{att.otLogout || "-"}</td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          att.status === "Present"
                                            ? "bg-success"
                                            : att.status === "Absent"
                                              ? "bg-danger"
                                              : att.status === "Holiday"
                                                ? "bg-info"
                                                : att.status === "Off"
                                                  ? "bg-success"
                                                  : "bg-warning"
                                        }`}
                                      >
                                        {att.status === "Leave" && att.leaveType
                                          ? `${att.leaveType}${att.session ? `, ${att.session}` : ""}`
                                          : att.status === "Off"
                                            ? "Off (Paid)"
                                            : att.status}
                                        {att.status === "Holiday" &&
                                        att.holidayName
                                          ? ` (${att.holidayName})`
                                          : ""}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 bg-light">
                      <div className="p-3">
                        <h6>Summary</h6>
                        <table className="table table-sm">
                          <tbody>
                            <tr>
                              <td>Salary</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.salary}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Total Working Days</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.totalWorkingDays}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Total Working Hours</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.totalWorkingHours}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Total Attendance</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.totalAttendance}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Total Leaves</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.totalLeaves}
                                  style={{ color: "red", fontWeight: "600" }}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Total Hours Worked</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.totalHoursWorked}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Pay for regular</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.payForRegular}
                                  style={{ color: "green", fontWeight: "600" }}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Total OT hours</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.totalOTHours}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Pay for OT</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.payForOT}
                                  style={{ color: "green", fontWeight: "600" }}
                                  readOnly
                                />
                              </td>
                            </tr>
                            {/* <tr>
                              <td>SSO Payment(5%)</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.ssoPayment}
                                  readOnly
                                />
                              </td>
                            </tr> */}
                            <tr>
                              <td>Bonus Add</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.bonusAdd || 0}
                                  style={{ color: "green", fontWeight: "600" }}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>SSO Payment(5%)</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.ssoPayment}
                                  style={{ color: "red", fontWeight: "600" }}
                                  readOnly
                                />
                              </td>
                            </tr>
                            {/* <tr>
                              <td>Loan Deduction</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.loanDeduction}
                                  readOnly
                                />
                              </td>
                            </tr> */}
                            <tr>
                              <td>Loan Deduction</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.loanDeduction || 0}
                                  style={{ color: "red", fontWeight: "600" }}
                                  readOnly
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Extra Pay</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  min="0"
                                  step="0.01"
                                  value={summary.extraPay ?? ""}
                                  style={{ color: "green", fontWeight: "600" }}
                                  disabled={isViewOnly}
                                  onChange={(e) => {
                                    let extraPayValue = e.target.value;

                                    // Allow empty
                                    if (extraPayValue === "") {
                                      setSummary((prev) => ({
                                        ...prev,
                                        extraPay: "",
                                        totalPayableAmount:
                                          Math.round(
                                            (prev.payForRegular +
                                              prev.payForOT +
                                              prev.bonusAdd -
                                              prev.ssoPayment -
                                              prev.loanDeduction) *
                                              100,
                                          ) / 100,
                                      }));
                                      return;
                                    }

                                    extraPayValue = parseFloat(extraPayValue);

                                    // Stop negative values
                                    if (
                                      isNaN(extraPayValue) ||
                                      extraPayValue < 0
                                    ) {
                                      extraPayValue = 0;
                                    }

                                    setSummary((prev) => ({
                                      ...prev,
                                      extraPay: extraPayValue,
                                      totalPayableAmount:
                                        Math.round(
                                          (prev.payForRegular +
                                            prev.payForOT +
                                            prev.bonusAdd +
                                            extraPayValue -
                                            prev.ssoPayment -
                                            prev.loanDeduction) *
                                            100,
                                        ) / 100,
                                    }));
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td>Total Payable Amount</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={summary.totalPayableAmount}
                                  readOnly
                                  style={{
                                    color: "green",
                                    fontWeight: "600",
                                    backgroundColor: "#f8f9fa",
                                  }}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  {!isViewOnly ? (
                    <form onSubmit={handleSubmit}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ margin: "inherit" }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCloseModal}
                      >
                        Close
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h5 className="wor_pay fs-6">Worker Payroll</h5>
          </div>
          <div className="card-body px-0 pt-0">
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th>Worker Name</th>
                    <th>Joining Date</th>
                    <th>Total Working Days</th>
                    <th>Personal Leave</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayroll.length > 0 ? (
                    filteredPayroll.map((payroll) => (
                      <tr key={payroll.id}>
                        <td>{payroll.workerName || "Unknown"}</td>
                        <td>{payroll.joinDt}</td>
                        <td>{payroll.workDays}</td>
                        <td>{payroll.persLeave}</td>
                        <td>
                          <button
                            title="View Details"
                            className="btn btn-sm btn-info"
                            onClick={() => {
                              // ✅ USE ID DIRECTLY (NO NAME MATCHING)
                              const resolvedWorkerId =
                                payroll.worker_id ??
                                state.workers?.find(
                                  (worker) =>
                                    worker.name === payroll.workerName,
                                )?.id ??
                                null;

                              if (
                                resolvedWorkerId === null ||
                                resolvedWorkerId === undefined
                              ) {
                                setMessage({
                                  text: "This payroll row has no linked worker ID.",
                                  type: "error",
                                });
                                return;
                              }

                              setSelectedworker_id(String(resolvedWorkerId));

                              if (payroll.startDate && payroll.endDate) {
                                setStartDate(payroll.startDate);
                                setEndDate(payroll.endDate);
                              }

                              setSummary((prev) => ({
                                ...prev,
                                extraPay: payroll.extra_pay || 0,
                              }));

                              setIsViewOnly(true);
                              setShowPayrollForm(true);
                            }}
                            style={{ fontSize: "13px", padding: "4px 10px" }}
                          >
                            <i className="fa-solid fa-circle-info"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">No payroll records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
}

export default Payroll;
